import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/Header";
import PropertyCard from "@/components/PropertyCard";
import { Property } from "@/types/property";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import API_URL from "@/config";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";
import useDocumentTitle from "@/components/Helmet";
import { useLocation } from "wouter";
import { LocationSearch } from "@/components/GoogleMap/LocationSearch";


const PageData: any = {
  rent: {
    title: "Rent Properties",
    category: "Rent"
  },
  sold: {
    title: "Sold Properties",
    category: "Sold"
  },
  share: {
    title: "Share Properties",
    category: "Share"
  },
  commercial: {
    title: "Commercial Properties",
    category: "Commercial"
  },
  "new-homes": {
    title: "New Homes Properties",
    category: "New Homes"
  }
}

export default function Commercial() {
  useDocumentTitle("Commercial Properties");
  const { toast } = useToast();
  const [location, navigate] = useLocation();
  const data = PageData[location.split("/")[1]];
  if (!data) {
    navigate("/");
  }

  const [filters, setFilters] = useState({
    propertyType: "",
    minPrice: "",
    maxPrice: "",
    city: "",
    search: "",
    sortBy: "date-desc" as const,
  });

  const [limit, setLimit] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [priceRange, setPriceRange] = useState(["", ""]);


  const fetchProperties = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        category: data.category,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== "")
        ),
        limit: limit.toString(),
        page: currentPage.toString(),
      });
      const res = await axios.get(API_URL + `/api/properties?${params}`)
        .then((res) => {
          setProperties(res?.data?.properties);
        })

    } catch (error: any) {
      toast({
        title: "Error!",
        description: error?.response?.data?.message,
      })
    }
    finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchProperties();
  }, [data, filters])

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput.split(",")[0] }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  useEffect(()=>{
    const handler = setTimeout(() => {
      setFilters(prev => ({ ...prev, minPrice: priceRange[0], maxPrice: priceRange[1] }));
    }, 500)

    return () => {
      clearTimeout(handler);
    }
  },[priceRange])

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Trigger refetch by updating filters
    setFilters(prev => ({ ...prev }));
    fetchProperties();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-blue-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{data.title}</h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Discover prime {data.title} real estate opportunities across Ireland
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              {/* <Input
                type="text"
                placeholder="Search location or property name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                data-testid="input-commercial-search"
              /> */}
              <div className="relative">
                <i className="fas fa-map-marker-alt absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <LocationSearch
                  value={searchInput}
                  onChange={setSearchInput}
                />
              </div>
            </div>

            <Select value={filters.propertyType} onValueChange={(value) => handleFilterChange("propertyType", value)}>
              <SelectTrigger data-testid="select-commercial-type">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="retail">Retail</SelectItem>
                <SelectItem value="industrial">Industrial</SelectItem>
                <SelectItem value="warehouse">Warehouse</SelectItem>
                <SelectItem value="restaurant">Restaurant</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="number"
              placeholder="Min Price"
              value={priceRange[0]}
              onChange={(e) => setPriceRange([e.target.value, priceRange[1]])}
              data-testid="input-min-price"
            />

            <Input
              type="number"
              placeholder="Max Price"
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], e.target.value])}
              data-testid="input-max-price"
            />

            <Button type="submit" data-testid="button-search-commercial">
              <i className="fas fa-search mr-2"></i>Search
            </Button>
          </form>
        </div>
      </section>

      {/* Results Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{data.title}</h2>
            <p className="text-gray-600" data-testid="text-commercial-results-count">
              {properties.length} properties found
            </p>
          </div>

          <Select value={filters.sortBy} onValueChange={(value) => handleFilterChange("sortBy", value)}>
            <SelectTrigger className="w-48" data-testid="select-sort-commercial">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Most Recent</SelectItem>
              <SelectItem value="date-asc">Oldest First</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : properties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {properties.map((property) => (
              <PropertyCard key={property._id} property={property} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <i className="fas fa-building text-6xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No commercial properties found
            </h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search criteria to find more properties.
            </p>
            <Button
              onClick={() => setFilters({ propertyType: "", minPrice: "", maxPrice: "", city: "", search: "", sortBy: "date-desc" })}
              data-testid="button-clear-filters"
            >
              Clear Filters
            </Button>
          </div>
        )}

        <div className="flex justify-center items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            data-testid="button-previous-page"
          >
            <i className="fas fa-chevron-left mr-2"></i>Previous
          </Button>
          <span className="px-4 py-2 text-gray-600" data-testid="text-current-page">
            Page {currentPage}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={properties.length < limit}
            data-testid="button-next-page"
          >
            Next<i className="fas fa-chevron-right ml-2"></i>
          </Button>
        </div>
      </section>
    </div>
  );
}
